import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import type { Type } from '@warp-drive/core-types/symbols';
import type UserModel from './user';
import type LocationModel from './location';
import type ItemModel from './item';
import type CompanyModel from './company';
import type StocktakingEntryModel from './stocktaking-entry';
import type SupplierItemModel from './supplier-item';
import OrderEntryModel from './order-entry';
const formatter = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 2,
});

export default class LocationItemModel extends Model {
  declare [Type]: 'location-item';
  @belongsTo('location', { async: false, inverse: 'locationItems' })
  declare location: LocationModel;
  @belongsTo('item', { async: false, inverse: 'locationItems' })
  declare item: ItemModel;
  @hasMany('stocktaking-entry', { async: false, inverse: 'location_item' })
  declare stocktakingEntries: StocktakingEntryModel[];

  get rounded_calculated_amount() {
    return Math.round(
      this.virtual_calculated_amount / this.item.container_size
    );
  }
  get displayAmount() {
    return formatter.format(
      this.virtual_calculated_amount / this.item.container_size
    );
  }
  get last_stocktaking_entry() {
    const ste = this.stocktakingEntries
      .filter((st: StocktakingEntryModel) => st.stocktaking.status == 'done')
      .sort(
        (a: StocktakingEntryModel, b: StocktakingEntryModel) =>
          +b.stocktaking.updated_at - +a.stocktaking.updated_at ||
          Number(b.stocktaking.id) - Number(a.stocktaking.id)
      );
    return ste[0];
  }
  get virtual_calculated_amount() {
    // last stocktaking + all orders which are arrived after
    // console.log('start', this.id);
    const last_ste = this.last_stocktaking_entry;
    if (!last_ste) {
      return 0;
    }
    const amount_last_st = last_ste.amount;
    const last_st = last_ste.stocktaking.updated_at;

    const orders_after_last_st = this.item.supplierItems
      .flatMap((supI) => supI.orderEntries)
      .filter(
        (oe: OrderEntryModel) =>
          oe.order &&
          oe.order.status == 'delivered' &&
          oe.order.delivered_to == this.location &&
          oe.order.accepted_date &&
          new Date(oe.order.accepted_date).getTime() >
            new Date(last_st).getTime()
      );

    const delivered_after_last_st =
      orders_after_last_st
        .map((oe: OrderEntryModel) => oe.amount_delivered)
        .reduce((prev, curr) => prev + (curr ? curr : 0), 0) ?? 0;
    const container_size = this.item.container_size;

    return amount_last_st + delivered_after_last_st * container_size;
  }

  @attr('number')
  declare proposal_amount: number;
  get displayProposalAmount() {
    const container_size = this.item.container_size;
    return formatter.format(this.proposal_amount / container_size);
  }
  get virtual_proposal_amount() {
    return Math.floor(this.proposal_amount / this.item.container_size);
  }
  set virtual_proposal_amount(value: number | string) {
    const newAmount = parseInt(value);
    if (Number.isNaN(newAmount)) {
      return;
    }
    this.proposal_amount =
      newAmount * this.item.container_size +
      this.virtual_proposal_amount_single;
  }
  get virtual_proposal_amount_single() {
    return (
      this.proposal_amount -
      this.virtual_proposal_amount * this.item.container_size
    );
  }
  set virtual_proposal_amount_single(value: number | string) {
    const newAmount = parseInt(value);
    if (Number.isNaN(newAmount)) {
      return;
    }
    this.proposal_amount =
      newAmount + this.virtual_proposal_amount * this.item.container_size;
  }
  get diff() {
    const container_size = this.item.container_size;
    const diff = Math.round(
      (this.proposal_amount - this.virtual_calculated_amount) / container_size
    );
    if (Number.isNaN(diff)) {
      return '(keine Zahl)';
    } else {
      return diff;
    }
  }

  @belongsTo('company', { async: false, inverse: null })
  declare company: CompanyModel;
  @attr()
  declare created_at: string;
  @attr()
  declare updated_at: string;
  @belongsTo('user', { async: false, inverse: null })
  declare created_by: UserModel;
  @belongsTo('user', { async: false, inverse: null })
  declare updated_by: UserModel;
  get shouldBeSaved() {
    const updated = this.hasDirtyAttributes && this.dirtyType === 'updated';
    const isNewAndChanged = this.isNew && this.proposal_amount !== 0;
    // console.log(updated, isNewAndChanged);
    return updated || isNewAndChanged;
  }
}
